import React, { useState } from "react";
import "../../styles/CustomDataTable.css";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

export interface Column {
  key: string;
  header: string;
  render?: (value: any, row: any, index: number) => React.ReactNode;
  className?: string;
  align?: "left" | "center" | "right";
  width?: string | number; // optional width
}

export interface CustomDataTableProps {
  data: any[];
  columns: Column[];
  pageSize?: number;
  showPagination?: boolean;
  className?: string;
  emptyMessage?: string;
  onRowClick?: (row: any, index: number) => void;
  rowClassName?: (row: any, index: number) => string;
  showSpinner?: boolean;
}

const CustomDataTable: React.FC<CustomDataTableProps> = ({
  data,
  columns,
  pageSize = 10,
  showPagination = true,
  className = "",
  emptyMessage = "No data available",
  onRowClick,
  rowClassName,
  showSpinner,
}) => {
  const [page, setPage] = useState(1);

  const pageCount = Math.ceil(data.length / pageSize);
  const pagedData = data.slice((page - 1) * pageSize, page * pageSize);

  const handleRowClick = (row: any, index: number) => {
    if (onRowClick) {
      onRowClick(row, index);
    }
  };

  const getColumnAlignment = (column: Column) => {
    switch (column.align) {
      case "center":
        return "center";
      case "right":
        return "right";
      default:
        return "left";
    }
  };

  const shimmerWidths = ["70%", "50%", "80%", "70%", "100%"]; // example pattern
  return (
    <div className={`custom-table__container ${className}`}>
      <div className="custom-table__wrapper">
      <table className="custom-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={column.className}
                style={{
                  textAlign: getColumnAlignment(column),
                  width: column.width,
                  maxWidth: column.width,
                  minWidth: column.width,
                }}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
        {showSpinner ? (
          // Show shimmer/loading state
          Array.from({ length: pageSize }).map((_, rowIndex) => {
            const width = shimmerWidths[rowIndex % shimmerWidths.length]; // cycle through widths
            return (
              <tr key={rowIndex} className="shimmer-row">
                <td colSpan={columns.length}>
                  <div className="shimmer-bar" style={{ width }} />
                </td>
              </tr>
            );
          })
        ) :pagedData.length > 0 ? (
            pagedData.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className={rowClassName ? rowClassName(row, rowIndex) : ""}
                onClick={() => handleRowClick(row, rowIndex)}
                style={{ cursor: onRowClick ? "pointer" : "default" }}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={column.className}
                    style={{
                      textAlign: getColumnAlignment(column),
                      width: column.width,
                      maxWidth: column.width,
                      minWidth: column.width,
                      whiteSpace: column.render ? "normal" : "nowrap",
                    }}
                  >
                    {column.render
                      ? column.render(
                          row[column.key],
                          row,
                          (page - 1) * pageSize + rowIndex
                        )
                      : row[column.key]}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr className="empty-row">
              <td colSpan={columns.length}>
                <div style={{marginTop: "30px"}}>{emptyMessage}</div>
              </td>
            </tr>
          )}
          </tbody>
        </table>
    </div>

      {showPagination && pageCount >= 1 && (
        <div className="pagination">
          <span className="pagination__info">
            Shows {pagedData.length} results of {data.length}
          </span>
          <div className="pagination__controls">
            <button
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
              disabled={page === 1}
              className="pagination__button"
            >
              <ArrowBackIosIcon sx={{ height: "10px", width: "10px" }} />
            </button>
            {Array.from({ length: pageCount }, (_, i) => {
              const pageNum = (i + 1).toString().padStart(2, "0");
              const isActive = page === i + 1;
              return (
                <span
                  key={i}
                  className={`pagination__pageNumber ${isActive ? "active" : ""}`}
                  onClick={() => setPage(i + 1)}
                >
                  {pageNum}
                </span>
              );
            })}
            <button
              onClick={() => setPage((p) => Math.min(p + 1, pageCount))}
              disabled={page === pageCount}
              className="pagination__button"
            >
              <ArrowForwardIosIcon sx={{ height: "10px", width: "10px" }} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDataTable;